<template>
  <v-dialog
    v-model="dialogModel"
    max-width="900px"
    persistent
    scrollable
    role="dialog"
    aria-labelledby="group-users-title"
  >
    <v-card class="group-users-card">
      <!-- Header with close button -->
      <div class="dialog-header">
        <div class="header-title" id="group-users-title">
          {{ $i18n.t("Manage Group Users") }}
        </div>
        <div class="actions-right">
          <v-btn icon flat @click="closeDialog" class="close-btn">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Group info -->
      <div class="group-info px-4 py-3">
        <div class="d-flex align-center">
          <div class="group-avatar mr-3">
            <span>{{ group.shortName }}</span>
          </div>
          <div>
            <div class="group-name">{{ group.fullName || group.shortName }}</div>
            <div class="group-meta">
              <v-chip small :color="getTypeColor(group.type)" text-color="white" class="type-badge">
                {{ $i18n.t(capitalize(group.type)) || $i18n.t("Default") }}
              </v-chip>
              <span class="mx-2">•</span>
              <span class="users-count">
                <v-icon small class="mr-1">people</v-icon>
                {{ group.users ? group.users.length : 0 }} {{ $i18n.t("users") }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <v-tabs v-model="activeTab" slider-color="primary" class="group-tabs">
        <v-tab>{{ $i18n.t("Add Users") }}</v-tab>
        <v-tab>{{ $i18n.t("Current Users") }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Add Users Tab -->
        <v-tab-item>
          <div class="tab-content over pa-4" style="overflow-y: auto; max-height: 400px;">
            <div class="section-title mb-3">{{ $i18n.t("Select Users to Add") }}</div>
            <div class="d-flex justify-space-between align-center">
              <v-autocomplete
                v-model="selectedUsers"
                :items="availableUsers"
                :label="$i18n.t('Search users')"
                item-text="text"
                item-value="value"
                multiple
                chips
                deletable-chips
                :loading="loadingUsers"
                :disabled="isLoading"
                :no-data-text="$i18n.t('No users available')"
                :aria-label="$i18n.t('Select users to add to group')"
                class="flex-grow-1 mr-2"
              >
                <template slot="selection" slot-scope="{ item, index }">
                  <v-chip v-if="index < 5" small close @input="removeSelectedUser(item.value)" class="user-chip">
                    {{ item.text }}
                  </v-chip>
                  <span v-if="index === 5" class="grey--text caption">
                    (+{{ selectedUsers.length - 5 }} {{ $i18n.t("more") }})
                  </span>
                </template>
                <template slot="item" slot-scope="{ item }">
                  <div class="user-item">
                    <v-avatar size="32" class="mr-2 grey lighten-3">
                      <span class="caption">{{ getInitials(item.text) }}</span>
                    </v-avatar>
                    <div class="user-details">
                      <div class="user-name">{{ item.text }}</div>
                      <div class="user-email">{{ item.email }}</div>
                    </div>
                  </div>
                </template>
              </v-autocomplete>
              <v-select
                v-model="defaultUserRoles"
                :items="roleOptions"
                item-text="text"
                item-value="value"
                dense
                flat
                background-color="transparent"
                class="ml-2 py-2"
                hide-details
                style="max-width: 250px;"
              >
                <template v-slot:selection="{ item }">
                  {{ item.text }}
                </template>
              </v-select>
            </div>

            <div class="selected-users mt-4" v-if="selectedUsers.length > 0">
              <div class="section-title mb-3">{{ $i18n.t("Selected Users") }} ({{ selectedUsers.length }})</div>
              <div class="user-list">
                <div v-for="userId in selectedUsers" :key="userId" class="selected-user-item">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2 grey lighten-3">
                      <span class="caption">{{ getInitials(getUserName(userId)) }}</span>
                    </v-avatar>
                    <div class="user-details ">
                      <div class="user-name">{{ getUserName(userId) }}</div>
                      <div class="user-email">{{ getUserEmail(userId) }}</div>
                    </div>
                  </div>

                  <v-select
                    v-model="userRoles[userId]"
                    :items="roleOptions"
                    item-text="text"
                    item-value="value"
                    solo
                    flat
                    background-color="transparent"
                    class="mx-2"
                    hide-details
                    style="max-width: 150px"
                  >
                    <template v-slot:selection="{ item }">
                      {{ item.text }}
                    </template>
                  </v-select>

                  <v-btn icon small @click="removeSelectedUser(userId)" class="remove-btn">
                    <v-icon small>close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </v-tab-item>

        <!-- Current Users Tab -->
        <v-tab-item>
          <div class="tab-content pa-4" style="overflow-y: auto; max-height: 400px;">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="section-title">{{ $i18n.t("Current Users") }} ({{ currentUsers.length }})</div>
              <v-text-field
                v-model="search"
                append-icon="search"
                :label="$i18n.t('Search')"
                single-line
                hide-details
                class="search-field"
                style="max-width: 250px"
              ></v-text-field>
            </div>

            <v-data-table
              :headers="headers"
              :items="currentUsers"
              :search="search"
              :loading="loadingUsers"
              :no-data-text="$i18n.t('No users in this group')"
              :loading-text="$i18n.t('Loading users...')"
              :rows-per-page-items="[10, 25, 50]"
              :rows-per-page-text="$i18n.t('Rows per page:')"
              class="user-table elevation-0"
            >
              <template v-slot:items="props">
                <tr>
                  <td>
                    <div class="d-flex align-center">
                      <v-avatar size="32" class="mr-2 grey lighten-3">
                        <span class="caption">{{ getInitials(props.item.name) }}</span>
                      </v-avatar>
                      <div class="user-details">
                        <div class="user-name">{{ props.item.name }}</div>
                        <div class="user-email">{{ props.item.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td>{{ props.item.email }}</td>
                  <td>
                    <div class="d-flex align-center">
                      <v-select
                        v-model="props.item.role"
                        :items="roleOptions"
                        item-text="text"
                        item-value="value"
                        :disabled="isUserRoleInherited(props.item) || isLoading"
                        dense
                        flat
                        background-color="transparent"
                        class="ml-2 py-2"
                        hide-details
                        style="max-width: 250px;"
                        @change="updateUserRole(props.item, $event)"
                      >
                        <template v-slot:selection="{ item }">
                          {{ item ? item.text : capitalize(props.item.role) }}
                        </template>
                      </v-select>

                      <v-tooltip bottom v-if="isUserRoleInherited(props.item)">
                        <template v-slot:activator="{ on }">
                          <div class="ml-2 role-source-indicator" v-on="on">
                            <v-icon small color="blue-grey lighten-3">link</v-icon>
                          </div>
                        </template>
                        <span>{{ $i18n.t("Inherited from parent group") }}</span>
                      </v-tooltip>
                    </div>
                  </td>
                  <td class="text-xs-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          small
                          @click="confirmRemoveUser(props.item)"
                          :disabled="isUserRoleInherited(props.item) || isLoading"
                          v-on="on"
                          :aria-label="$i18n.t('Remove user from group')"
                        >
                          <v-icon small>delete</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $i18n.t("Remove") }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>
      </v-tabs-items>

      <v-divider></v-divider>
      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn flat @click="closeDialog" :disabled="isLoading" :aria-label="$i18n.t('Cancel')">
          {{ $i18n.t("Cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          @click="saveUsers"
          :loading="isLoading"
          :disabled="!hasChanges"
          :aria-label="$i18n.t('Save changes')"
        >
          {{ $i18n.t("Save Changes") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from "vuex";
import { GROUP_TYPE_COLOR, USER_TYPE, EXPERT_ROLE, BENEFICIARY_ROLES, BENEFICIARY_ROLE_LIST } from "../../constants";

export default {
  name: "GroupUsers",
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    group: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedUsers: [],
      originalUsers: [],
      userRoles: {}, // Maps userId to role for new users
      roleChanges: [], // Tracks role changes for existing users
      loadingUsers: false,
      isLoading: false,
      search: "",
      activeTab: 0,
      defaultRole: BENEFICIARY_ROLES.VIEWER,
      confirmDialog: {
        show: false,
        title: "",
        message: "",
        action: null,
        data: null
      },
      defaultUserRoles: "viewer",
      headers: [
        {
          text: this.$i18n.t("User"),
          align: "left",
          value: "name",
          sortable: true
        },
        {
          text: this.$i18n.t("Email"),
          value: "email",
          sortable: true
        },
        {
          text: this.$i18n.t("Role"),
          value: "role",
          sortable: true
        },
        {
          text: this.$i18n.t("Actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "100px"
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      allUsers: "users/getUsers"
    }),
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      }
    },
    currentUsers() {
      // Process current group users with role information
      return (this.group.users || [])
        .map(user => {
          // Handle both string user IDs and object format
          const userObject = typeof user === "object" ? user : { userInfo: user, role: null };
          const fullUser = this.allUsers.find(u => u.user === (userObject.userInfo || user));

          if (!fullUser) return null;

          // Assign default role based on user type
          let defaultRole;
          if (fullUser.type === USER_TYPE.BENEFICIARY) {
            defaultRole = BENEFICIARY_ROLE_LIST.VIEWER;
          } else {
            defaultRole = BENEFICIARY_ROLE_LIST.VIEWER;
          }

          return {
            ...fullUser,
            userId: userObject.userId || user,
            role: userObject.role || defaultRole,
            source: userObject.source || "direct",
            inheritedFrom: userObject.inheritedFrom || null
          };
        })
        .filter(Boolean);
    }, // Remove null entries
    availableUsers() {
      // Users not in the group, available to add
      console.log("Available users:", this.allUsers);
      console.log("Current users:", this.currentUsers);
      const currentUserIds = new Set(this.currentUsers.map(u => u._id));
      return this.allUsers
        .filter(user => !currentUserIds.has(user._id))
        .filter(user => user.type === USER_TYPE.BENEFICIARY)
        .filter(user => !this.selectedUsers.includes(user.user))
        .map(user => ({
          text: user.name,
          value: user.user,
          email: user.email
        }));
    },
    roleOptions() {
      // Combine all possible roles based on user type
      const roles = [];

      Object.values(BENEFICIARY_ROLE_LIST).forEach(role => {
        roles.push({
          text: this.capitalize(role),
          value: role,
          type: USER_TYPE.BENEFICIARY
        });
      });

      return roles;
    },

    userRoleOptions() {
      return userType => {
        if (userType === USER_TYPE.BENEFICIARY) {
          return this.roleOptions.filter(role => role.type === USER_TYPE.BENEFICIARY);
        }
        return this.roleOptions;
      };
    },
    getUserCount() {
      return this.currentUsers.length;
    },
    hasChanges() {
      // Check if there are any changes to save
      if (this.originalUsers.length !== this.selectedUsers.length) return true;

      // Check for role changes
      if (this.roleChanges.length > 0) return true;

      // Check if any users were added or removed
      const originalSet = new Set(this.originalUsers);
      const currentSet = new Set(this.selectedUsers);

      for (const userId of originalSet) {
        if (!currentSet.has(userId)) return true;
      }

      for (const userId of currentSet) {
        if (!originalSet.has(userId)) return true;
      }

      return false;
    }
  },
  created() {
    this.fetchUsers();
    this.$watch("selectedUsers", newUsers => {
      newUsers.forEach(userId => {
        if (!this.userRoles[userId]) {
          let defaultRole = this.defaultUserRoles;

          this.$set(this.userRoles, userId, defaultRole);
        }
      });
    });
  },
  methods: {
    getTypeColor(type) {
      return GROUP_TYPE_COLOR[type] || "grey";
    },
    getRoleColor(role) {
      const colorMap = {
        // Expert roles
        [EXPERT_ROLE.ADMIN]: "red darken-2",
        [EXPERT_ROLE.EXPERT]: "orange darken-2",

        // Beneficiary roles
        [BENEFICIARY_ROLE_LIST.VIEWER]: "blue darken-1",
        [BENEFICIARY_ROLE_LIST.CUSTOMER]: "green darken-1",
        [BENEFICIARY_ROLE_LIST.OPERATIONAL_MANAGER]: "purple darken-1",
        [BENEFICIARY_ROLE_LIST.CONTRACT_MANAGER]: "deep-purple darken-1"
      };

      return colorMap[role] || "grey darken-1";
    },
    isUserRoleInherited(user) {
      return user.source === "inherited";
    },
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        await this.$store.dispatch("users/fetchUsers");
      } catch (error) {
        console.error("Error fetching users:", error);
        // Show error notification
        this.$store.dispatch("ui/displaySnackbar", {
          message: this.$i18n.t("Error loading users"),
          color: "error",
          role: "alert"
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    removeSelectedUser(userId) {
      const updatedUsers = this.group.users.filter(user => user.userInfo !== userId);
      this.group.users = updatedUsers;
      // Add the user to the usersToRemove list
      if (!this.originalUsers.includes(userId)) {
        this.originalUsers.push(userId);
      }
    },
    confirmRemoveUser(user) {
      this.removeSelectedUser(user.user);
    },
    getRoleClass(role) {
      const roleClasses = {
        admin: "role-admin",
        manager: "role-manager",
        supervisor: "role-supervisor",
        user: "role-user"
      };

      return roleClasses[role] || "role-user";
    },
    getUserName(userId) {
      const user = this.allUsers.find(u => u.user === userId);
      return user ? user.name : userId;
    },
    getUserEmail(userId) {
      const user = this.allUsers.find(u => u.user === userId);
      return user ? user.email : "";
    },
    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map(word => word.charAt(0).toUpperCase())
        .join("")
        .substring(0, 2);
    },
    getUserType(userId) {
      const user = this.allUsers.find(u => u.user === userId);
      return user ? user.type : USER_TYPE.BENEFICIARY;
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    updateUserRole(user, newRole) {
      const currentUser = this.group.users.find(u => u.userInfo === user.user);
      console.log("Updating role for user:", currentUser, "to role:", newRole, user, this.group.users);
      if (currentUser.role !== newRole) {
        this.roleChanges.push({ userId: currentUser.userInfo, role: newRole });
      } else {
        this.roleChanges = this.roleChanges.filter(change => change.userId !== user.userId);
      }
    },
    async saveUsers() {
      this.isLoading = true;

      try {
        // Get users to add (those in selectedUsers but not in originalUsers)
        const usersToAdd = this.selectedUsers
          .filter(userId => !this.originalUsers.includes(userId))
          .map(userId => ({
            userInfo: userId,
            role: this.userRoles[userId] || this.defaultUserRoles
          }));

        // Get users to remove (those in originalUsers but not in selectedUsers)
        const usersToRemove = this.originalUsers.filter(userId => !this.selectedUsers.includes(userId));

        // Process additions with roles
        if (usersToAdd.length > 0) {
          await this.$store.dispatch("group/addUsersToGroup", {
            groupId: this.group._id,
            users: usersToAdd
          });
        }

        // Process removals
        if (usersToRemove.length > 0) {
          console.log("Removing users:", usersToRemove);
          await this.$store.dispatch("group/removeUsersFromGroup", {
            groupId: this.group._id,
            userIds: usersToRemove
          });
        }

        // Process role changes for existing users
        for (const change of this.roleChanges) {
          console.log("Updating user role:", change);
          await this.$store.dispatch("group/updateUserRole", {
            groupId: this.group._id,
            userId: change.userId,
            role: change.role
          });
        }

        this.$emit("users-updated");
        this.$store.dispatch("ui/displaySnackbar", {
          message: this.$i18n.t("Group users updated successfully"),
          color: "success"
        });
        this.closeDialog();
      } catch (error) {
        // Show error notification
        this.$store.dispatch("ui/displaySnackbar", {
          message: this.$i18n.t("Error saving group users"),
          color: "error",
          role: "alert"
        });
      } finally {
        this.isLoading = false;
      }
    },
    closeDialog() {
      this.$emit("close");
    }
  }
};
</script>

<style scoped>
.group-users-card {
  border-radius: 4px;
  overflow: hidden;
}
.tab-content {
  overflow: scroll;
}
.dialog-header {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.header-title {
  flex: 1;
  font-size: 18px;
  font-weight: 500;
  color: #333;
  text-align: center;
}

.actions-right {
  display: flex;
}

.group-info {
  border-bottom: 1px solid #f0f0f0;
}

.group-avatar {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: #333;
}

.group-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.group-meta {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.users-count {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: 15px;
  font-weight: 500;
  color: #333;
}

.user-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 14px;
  color: #333;
}

.user-email {
  font-size: 12px;
  color: #666;
}

.user-chip {
  background-color: #f5f5f5 !important;
  color: #333 !important;
}

.selected-user-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-radius: 4px;
  background-color: #f9f9f9;
  margin-bottom: 8px;
}

.status-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-department {
  background-color: #ff9800;
}

.status-team {
  background-color: #4caf50;
}

.status-division {
  background-color: #2196f3;
}

.status-branch {
  background-color: #9c27b0;
}

.status-default {
  background-color: #9e9e9e;
}

.role-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.role-admin {
  background-color: #ffebee;
  color: #d32f2f;
}

.role-manager {
  background-color: #fff3e0;
  color: #f57c00;
}

.role-supervisor {
  background-color: #e8f5e9;
  color: #388e3c;
}

.role-user {
  background-color: #e3f2fd;
  color: #1976d2;
}

.user-table {
  width: 100%;
}

/* Focus styles for accessibility */
.v-btn:focus {
  outline: 1px solid rgba(0, 0, 0, 0.2);
  outline-offset: 1px;
}
</style>
